import { useState } from "react";
import PropTypes from "prop-types";
import { Link, useSearchParams } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import LinearProgress from "@mui/material/LinearProgress";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import PageGuide from "components/admin/PageGuide";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import GiftEditor, { GiftPreview } from "components/admin/GiftEditor";
import TemplatePicker from "components/admin/TemplatePicker";
import PlayerSearch from "components/admin/PlayerSearch";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useAuth } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate, formatNumber } from "utils/format";

const BASE = "/api/admin/world-boss";

/** WorldBossRewardRow.KIND_* */
const KIND_RANK = 1;
const KIND_MILESTONE = 2;
const KIND_CLAN = 3;
const KINDS = {
  [KIND_RANK]: { label: "Hạng", color: "info" },
  [KIND_MILESTONE]: { label: "Mốc toàn server (đã bỏ)", color: "default", retired: true },
  [KIND_CLAN]: { label: "Bang", color: "warning" },
};

/** WorldBossManager.STATUS_* */
const STATUSES = {
  TIMEOUT: { label: "Hết giờ", color: "success" },
  SHUTDOWN: { label: "Bảo trì", color: "warning" },
  ADMIN_END: { label: "Admin kết thúc", color: "secondary" },
};

const DAY_NAMES = { 1: "T2", 2: "T3", 3: "T4", 4: "T5", 5: "T6", 6: "T7", 7: "CN" };

function clock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function dayText(weekdays) {
  const days = String(weekdays || "")
    .split(",")
    .map((d) => DAY_NAMES[d.trim()])
    .filter(Boolean);
  return days.length ? days.join(", ") : "Chỉ khi Chủ nhật bốc trúng";
}

function useForm(initial) {
  const [form, setForm] = useState(initial);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const num = (key, label, xs = 6, md = 3, props = {}) => (
    <Grid item xs={xs} md={md} key={key}>
      <MDInput
        fullWidth
        type="number"
        label={label}
        value={form[key] ?? ""}
        inputProps={{ step: "any", ...props }}
        onChange={(e) => set({ [key]: e.target.value })}
      />
    </Grid>
  );
  return { form, set, num };
}

/** Liệt kê "• Nhãn: cũ → mới" cho các ô số / chữ đã đổi. */
function diffLines(before, after, fields) {
  return fields
    .filter(([key]) => String(before?.[key] ?? "") !== String(after?.[key] ?? ""))
    .map(([key, label]) => {
      const show = (v) =>
        typeof v === "number" || /^-?\d+(\.\d+)?$/.test(String(v))
          ? formatNumber(Number(v))
          : String(v ?? "");
      return `• ${label}: ${show(before?.[key])} → ${show(after?.[key])}`;
    });
}

/** Gửi và đóng hộp khi server nhận. `ask` (nếu có) là nội dung hộp hỏi lại trước khi gửi. */
function useSubmit(onClose, onDone) {
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const submit = async (action, ask) => {
    if (ask && !(await confirm(ask))) return;
    const { ok } = await run(action, (text) => text);
    if (ok) {
      onClose();
      onDone();
    }
  };
  return [submit, busy];
}

function SummonDialog({ templates, onClose, onDone }) {
  const enabled = templates.filter((t) => t.loaded);
  const { form, set, num } = useForm({ bossId: enabled[0]?.bossId || 1, minutes: 17 });
  const [submit, busy] = useSubmit(onClose, onDone);
  const bossName = enabled.find((t) => t.bossId === Number(form.bossId))?.name || "Boss";
  return (
    <ActionDialog
      open
      title="Gọi boss thế giới ngay"
      description="Mở thêm một lượt ngoài lịch. Hết giờ thì chốt hạng và gửi thưởng như lượt thường."
      color="error"
      submitText="Gọi boss…"
      disabled={!(Number(form.minutes) >= 1 && Number(form.minutes) <= 180)}
      busy={busy}
      onClose={onClose}
      onSubmit={() =>
        submit(
          () =>
            api.post(`${BASE}/summon`, {
              bossId: Number(form.bossId),
              minutes: Number(form.minutes),
              goal: 0,
            }),
          {
            title: "Gọi boss thế giới",
            message: [
              `${bossName} xuất hiện ngay ở Vực Hỗn Mang trong ${form.minutes} phút.`,
              "",
              "• Mọi người chơi đang online thấy banner báo boss.",
              "• Hết giờ: chốt hạng và gửi thư thưởng như lượt theo lịch.",
            ].join("\n"),
            color: "error",
            confirmText: "Gọi boss",
          }
        )
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <MDInput
            select
            fullWidth
            label="Boss"
            value={form.bossId}
            onChange={(e) => set({ bossId: Number(e.target.value) })}
            SelectProps={{ sx: { height: 40 } }}
          >
            {enabled.map((t) => (
              <MenuItem key={t.bossId} value={t.bossId}>
                {t.name}
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        {num("minutes", "Boss ở lại (phút, 1 - 180)", 12, 6, { min: 1, max: 180 })}
        <Grid item xs={12}>
          <MDBox display="flex" gap={1} flexWrap="wrap">
            {[10, 17, 30, 60].map((m) => (
              <Chip
                key={m}
                size="small"
                color={Number(form.minutes) === m ? "info" : "default"}
                variant={Number(form.minutes) === m ? "filled" : "outlined"}
                label={`${m} phút`}
                onClick={() => set({ minutes: m })}
              />
            ))}
          </MDBox>
        </Grid>
      </Grid>
    </ActionDialog>
  );
}

SummonDialog.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

/** Mục tiêu sát thương không còn dùng (đã bỏ thưởng mốc): không hiện, lưu cấu hình thì gửi lại giá trị đang có */
const HIDDEN_CONFIG_FIELDS = [
  "goalStart",
  "goalMin",
  "goalMax",
  "goalRatio",
  "goalStepMin",
  "goalStepMax",
];

const CONFIG_FIELDS = [
  ["displayHp", "Máu hiển thị (không giảm)"],
  ["phase2Min", "Pha 2 từ phút"],
  ["phase3Min", "Pha 3 từ phút"],
  ["enrageAtkPct", "Nổi giận +% ATK"],
  ["maxRound", "Lượt mỗi trận"],
  ["fightCooldownSec", "Chờ giữa hai trận (giây)"],
  ["minPetLvl", "Cấp pet tối thiểu"],
  ["minFights", "Số trận để nhận thưởng"],
  ["minDamagePermille", "Hoặc ‰ tổng sát thương"],
  ["zoneCapacity", "Người mỗi khu"],
  ["warnMinutes", "Báo trước (phút)"],
  ["eventMinutes", "Khung event hiển thị (phút)"],
  ["mailExpireDays", "Hạn thư (ngày)"],
  ["clanMinMembers", "Bang cần số người đủ điều kiện"],
  ["titleWeekId", "Danh hiệu Bá Chủ (id)"],
  ["titleWeekDays", "Hạn danh hiệu tuần (ngày)"],
];

function ConfigDialog({ config, onClose, onDone }) {
  const { form, set, num } = useForm({ ...config });
  const [submit, busy] = useSubmit(onClose, onDone);
  const body = () => {
    const out = { damageMulSteps: form.damageMulSteps };
    HIDDEN_CONFIG_FIELDS.forEach((key) => {
      out[key] = Number(form[key]);
    });
    CONFIG_FIELDS.forEach(([key]) => {
      out[key] = Number(form[key]);
    });
    return out;
  };
  return (
    <ActionDialog
      open
      maxWidth="md"
      title="Cấu hình boss thế giới"
      description="Lượt đang chạy áp dụng ngay hệ số, pha, thời gian chờ và điều kiện thưởng mới. Máy chủ kiểm cả bộ trước khi lưu; sai một ô thì không lưu gì."
      color="warning"
      submitText="Lưu…"
      busy={busy}
      onClose={onClose}
      onSubmit={() => {
        const changes = diffLines(config, form, [
          ...CONFIG_FIELDS,
          ["damageMulSteps", "Hệ số theo thời gian còn lại"],
        ]);
        if (changes.length === 0) {
          onClose();
          return;
        }
        submit(() => api.put(`${BASE}/config`, body()), {
          title: "Lưu cấu hình boss thế giới",
          message: [...changes, "", "Lượt đang chạy (nếu có) dùng ngay số mới."].join("\n"),
          color: "warning",
          confirmText: "Lưu",
        });
      }}
    >
      <Grid container spacing={2}>
        {CONFIG_FIELDS.map(([key, label]) => num(key, label, 6, 4))}
        <Grid item xs={12}>
          <MDInput
            fullWidth
            label="Hệ số sát thương theo thời gian còn lại, JSON [[giây còn lại, hệ số], ...]"
            value={form.damageMulSteps}
            onChange={(e) => set({ damageMulSteps: e.target.value })}
          />
        </Grid>
      </Grid>
    </ActionDialog>
  );
}

ConfigDialog.propTypes = {
  config: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function prettySkills(raw) {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch (e) {
    return raw;
  }
}

function TemplateDialog({ template, onClose, onDone }) {
  const { form, set, num } = useForm(() => ({
    ...template,
    skills: prettySkills(template.skills),
  }));
  const [submit, busy] = useSubmit(onClose, onDone);
  const [showSkills, setShowSkills] = useState(false);
  const days = String(form.weekdays || "")
    .split(",")
    .map((d) => Number(d.trim()))
    .filter((d) => d >= 1 && d <= 7);
  const toggleDay = (d) => {
    const next = days.includes(d) ? days.filter((x) => x !== d) : [...days, d];
    set({ weekdays: next.sort((a, b) => a - b).join(",") });
  };
  const body = () => ({
    petId: Number(form.petId),
    name: form.name,
    lvl: Number(form.lvl),
    atk: Number(form.atk),
    def: Number(form.def),
    weekdays: form.weekdays,
    skills: form.skills,
    enabled: Boolean(form.enabled),
  });
  return (
    <ActionDialog
      open
      maxWidth="md"
      title={`Sửa boss: ${template.name}`}
      description="Lượt đang chạy giữ chỉ số cũ, lượt sau dùng số mới."
      color="warning"
      submitText="Lưu…"
      busy={busy}
      onClose={onClose}
      onSubmit={() => {
        const changes = [
          ...diffLines(template, form, [
            ["name", "Tên"],
            ["petId", "Hình (mã pet)"],
            ["lvl", "Cấp"],
            ["atk", "Công"],
            ["def", "Thủ"],
          ]),
          String(template.weekdays || "") !== String(form.weekdays || "") &&
            `• Ra vào: ${dayText(template.weekdays)} → ${dayText(form.weekdays)}`,
          Boolean(template.enabled) !== Boolean(form.enabled) &&
            `• ${form.enabled ? "Bật" : "TẮT — boss này không ra nữa"}`,
          prettySkills(template.skills) !== form.skills && "• Kỹ năng",
        ].filter(Boolean);
        submit(() => api.put(`${BASE}/templates/${template.bossId}`, body()), {
          title: `Lưu boss ${template.name}`,
          message: [...changes, "", "Lượt sau dùng bản mới."].join("\n"),
          color: "warning",
          confirmText: "Lưu",
        });
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <MDInput
            fullWidth
            label="Tên"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TemplatePicker
            kind="pet"
            fullWidth
            label="Hình dạng (gõ tên pet)"
            value={form.petId}
            onChange={(id) => set({ petId: Number(id) || 0 })}
          />
        </Grid>
        {num("lvl", "Cấp", 4, 4)}
        {num("atk", "Công", 4, 4)}
        {num("def", "Thủ", 4, 4)}
        <Grid item xs={12}>
          <MDTypography variant="caption" color="text" display="block" mb={0.5}>
            Ra vào các ngày (Chủ nhật luôn bốc ngẫu nhiên trong các boss đang bật)
          </MDTypography>
          <MDBox display="flex" gap={1} flexWrap="wrap">
            {[1, 2, 3, 4, 5, 6, 7].map((d) => (
              <Chip
                key={d}
                label={DAY_NAMES[d]}
                color={days.includes(d) ? "info" : "default"}
                variant={days.includes(d) ? "filled" : "outlined"}
                onClick={() => toggleDay(d)}
              />
            ))}
          </MDBox>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.enabled)}
                onChange={(e) => set({ enabled: e.target.checked })}
              />
            }
            label="Đang bật (tắt thì boss này không ra nữa)"
          />
        </Grid>
        <Grid item xs={12}>
          <MDButton
            size="small"
            variant="text"
            color="secondary"
            onClick={() => setShowSkills((v) => !v)}
          >
            <Icon>{showSkills ? "expand_less" : "expand_more"}</Icon>&nbsp;Kỹ năng (kỹ thuật)
          </MDButton>
          <Collapse in={showSkills}>
            <MDTypography variant="caption" color="text" display="block" my={1}>
              Ô 1 dùng từ pha 1, ô 2 từ pha 2, ô 3 là kỹ năng riêng pha 3; info là [mã
              ItemInfo.Type, giá trị]. Sai định dạng thì máy chủ không lưu.
            </MDTypography>
            <MDInput
              fullWidth
              multiline
              minRows={8}
              label="Kỹ năng (JSON)"
              value={form.skills}
              onChange={(e) => set({ skills: e.target.value })}
              inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
            />
          </Collapse>
        </Grid>
      </Grid>
    </ActionDialog>
  );
}

TemplateDialog.propTypes = {
  template: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function ScheduleDialog({ schedule, onClose, onDone }) {
  const { form, set, num } = useForm(
    schedule || { hour: 12, minute: 0, durationMin: 17, enabled: true }
  );
  const [submit, busy] = useSubmit(onClose, onDone);
  const body = () => ({
    hour: Number(form.hour),
    minute: Number(form.minute),
    durationMin: Number(form.durationMin),
    enabled: Boolean(form.enabled),
  });
  return (
    <ActionDialog
      open
      title={`${schedule ? "Sửa" : "Thêm"} khung giờ`}
      description="Các khung bật không được chồng giờ nhau (tính cả phút báo trước). Lượt đang chạy giữ giờ kết thúc cũ."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          schedule
            ? api.put(`${BASE}/schedules/${schedule.id}`, body())
            : api.post(`${BASE}/schedules`, body())
        )
      }
    >
      <Grid container spacing={2}>
        {num("hour", "Giờ", 4, 4, { min: 0, max: 23 })}
        {num("minute", "Phút", 4, 4, { min: 0, max: 59 })}
        {num("durationMin", "Ở lại (phút)", 4, 4, { min: 1, max: 180 })}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.enabled)}
                onChange={(e) => set({ enabled: e.target.checked })}
              />
            }
            label="Đang bật"
          />
        </Grid>
      </Grid>
    </ActionDialog>
  );
}

ScheduleDialog.defaultProps = { schedule: null };

ScheduleDialog.propTypes = {
  schedule: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function RewardDialog({ reward, onClose, onDone }) {
  const { form, set, num } = useForm(
    reward || { kind: KIND_RANK, rankFrom: 1, rankTo: 1, gift: [], stone: 0, enabled: true }
  );
  const [submit, busy] = useSubmit(onClose, onDone);
  const kind = Number(form.kind);
  const body = () => ({
    kind,
    rankFrom: Number(form.rankFrom),
    rankTo: Number(form.rankTo),
    gift: form.gift || [],
    stone: Number(form.stone),
    enabled: Boolean(form.enabled),
  });
  return (
    <ActionDialog
      open
      maxWidth="md"
      title={`${reward ? "Sửa" : "Thêm"} dòng thưởng`}
      description="Cá nhân: người đủ điều kiện có hạng trong khoảng này nhận quà qua thư (Đến hạng = 0 nghĩa là mọi hạng còn lại). Bang: linh thạch vào kho bang theo hạng bang."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          reward
            ? api.put(`${BASE}/rewards/${reward.id}`, body())
            : api.post(`${BASE}/rewards`, body())
        )
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <MDInput
            select
            fullWidth
            label="Loại"
            value={kind}
            onChange={(e) => set({ kind: Number(e.target.value) })}
            SelectProps={{ sx: { height: 40 } }}
          >
            {Object.entries(KINDS)
              .filter(([, v]) => !v.retired)
              .map(([k, v]) => (
                <MenuItem key={k} value={Number(k)}>
                  {v.label}
                </MenuItem>
              ))}
          </MDInput>
        </Grid>
        {num("rankFrom", kind === KIND_MILESTONE ? "Mốc (% mục tiêu)" : "Từ hạng", 6, 2)}
        {kind !== KIND_MILESTONE && num("rankTo", "Đến hạng (0 = còn lại)", 6, 3)}
        {kind === KIND_CLAN && num("stone", "Linh thạch", 6, 3)}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.enabled)}
                onChange={(e) => set({ enabled: e.target.checked })}
              />
            }
            label="Đang áp dụng"
          />
        </Grid>
      </Grid>
      {kind !== KIND_CLAN && (
        <>
          <Divider sx={{ my: 2 }} />
          <GiftEditor
            value={form.gift}
            onChange={(gift) => set({ gift })}
            label="Quà gửi vào thư"
          />
        </>
      )}
    </ActionDialog>
  );
}

RewardDialog.defaultProps = { reward: null };

RewardDialog.propTypes = {
  reward: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function ShopDialog({ row, limitTypes, onClose, onDone }) {
  const { form, set, num } = useForm(
    row || {
      itemId: 292011,
      count: 1,
      price: 10,
      limitType: 2,
      limitCount: 1,
      sort: 0,
      enabled: true,
    }
  );
  const [submit, busy] = useSubmit(onClose, onDone);
  const body = () => ({
    itemId: Number(form.itemId),
    count: Number(form.count),
    price: Number(form.price),
    limitType: Number(form.limitType),
    limitCount: Number(form.limitCount),
    sort: Number(form.sort),
    enabled: Boolean(form.enabled),
  });
  return (
    <ActionDialog
      open
      title={`${row ? "Sửa" : "Thêm"} hàng shop Hỗn Mang`}
      description="Người chơi trả bằng Hỗn Mang Tinh. Mọi món mua ra đều không giao dịch được."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          row ? api.put(`${BASE}/shop/${row.id}`, body()) : api.post(`${BASE}/shop`, body())
        )
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TemplatePicker
            kind="item"
            fullWidth
            hideAdminItems
            label="Vật phẩm (gõ tên)"
            value={form.itemId}
            onChange={(id) => set({ itemId: Number(id) || 0 })}
          />
        </Grid>
        {num("count", "Số lượng", 6, 4, { min: 1 })}
        {num("price", "Giá (Hỗn Mang Tinh)", 6, 4, { min: 1 })}
        <Grid item xs={6} md={4}>
          <MDInput
            select
            fullWidth
            label="Giới hạn"
            value={Number(form.limitType)}
            onChange={(e) => set({ limitType: Number(e.target.value) })}
            SelectProps={{ sx: { height: 40 } }}
          >
            {limitTypes.map((l) => (
              <MenuItem key={l.value} value={l.value}>
                {l.label}
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        {num("limitCount", "Số lượt", 6, 4, { min: 0 })}
        {num("sort", "Thứ tự", 6, 4)}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.enabled)}
                onChange={(e) => set({ enabled: e.target.checked })}
              />
            }
            label="Đang bán"
          />
        </Grid>
      </Grid>
    </ActionDialog>
  );
}

ShopDialog.defaultProps = { row: null };

ShopDialog.propTypes = {
  row: PropTypes.object,
  limitTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function HistoryDialog({ id, onClose }) {
  const detail = useFetch(() => api.get(`${BASE}/history/${id}`), [id]);
  const h = detail.data;
  const [tab, setTab] = useState(0);
  return (
    <ActionDialog
      open
      maxWidth="md"
      title={h ? `${h.bossName} · ${formatDate(h.startAt)}` : "Lượt boss"}
      description={h ? `Tổng ${formatNumber(h.total)}, ${h.players} người, ${h.fights} trận` : ""}
      color="info"
      submitText="Đóng"
      onClose={onClose}
      onSubmit={onClose}
    >
      {detail.error && <Alert severity="error">{detail.error}</Alert>}
      <Tabs value={tab} onChange={(e, v) => setTab(v)}>
        <Tab label="Cá nhân" />
        <Tab label="Bang" />
      </Tabs>
      {tab === 0 ? (
        <AdminTable
          loading={detail.loading}
          rows={h?.top?.players || []}
          rowKey="uid"
          emptyText="Không ai gây sát thương"
          columns={[
            { key: "rank", label: "Hạng", align: "center" },
            {
              key: "name",
              label: "Người chơi",
              render: (r) => <Link to={`/players/${r.uid}`}>{r.name}</Link>,
            },
            {
              key: "damage",
              label: "Sát thương",
              align: "right",
              render: (r) => formatNumber(r.damage),
            },
            { key: "fights", label: "Trận", align: "center" },
          ]}
        />
      ) : (
        <AdminTable
          loading={detail.loading}
          rows={h?.top?.clans || []}
          rowKey="clanId"
          emptyText="Không bang nào đủ điều kiện"
          columns={[
            { key: "rank", label: "Hạng", align: "center" },
            { key: "name", label: "Bang" },
            {
              key: "damage",
              label: "Sát thương",
              align: "right",
              render: (r) => formatNumber(r.damage),
            },
            { key: "eligible", label: "Đủ điều kiện", align: "center" },
          ]}
        />
      )}
    </ActionDialog>
  );
}

HistoryDialog.propTypes = {
  id: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};

function PlayerCard() {
  const [who, setWho] = useState(null);
  const [run] = useRunner();
  const [data, setData] = useState(null);
  const pick = async (person) => {
    setWho(person);
    setData(null);
    const { ok, result } = await run(() => api.get(`${BASE}/players/${person.userId}`));
    if (ok) setData(result);
  };
  return (
    <PageCard
      title="Tra người chơi"
      subtitle="Khách hỏi vì sao không có thưởng: xem họ đánh những lượt nào, hạng mấy"
      color="dark"
    >
      <PlayerSearch onPick={pick} />
      {who && data && (
        <MDBox mt={2}>
          {who.name && (
            <MDTypography variant="button" display="block" mb={0.5}>
              <Link to={`/players/${who.userId}`}>{who.name}</Link>
            </MDTypography>
          )}
          <MDTypography variant="caption" color="text" display="block" mb={1}>
            Sát thương theo tuần:{" "}
            {data.weeks.length
              ? data.weeks.map((w) => `${w.weekKey}: ${formatNumber(w.damage)}`).join(" · ")
              : "chưa có"}
          </MDTypography>
          <AdminTable
            rows={data.sessions}
            emptyText="Chưa đánh boss thế giới lần nào"
            columns={[
              { key: "time", label: "Lúc", render: (r) => formatDate(r.time) },
              { key: "bossName", label: "Boss" },
              { key: "rank", label: "Hạng", align: "center" },
              {
                key: "damage",
                label: "Sát thương",
                align: "right",
                render: (r) => formatNumber(r.damage),
              },
            ]}
          />
        </MDBox>
      )}
    </PageCard>
  );
}

function LiveCard({ data, canEdit, onReload }) {
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const [dialog, setDialog] = useState(null);
  const live = data.live;

  const end = async () => {
    const yes = await confirm({
      title: "Kết thúc lượt boss ngay",
      message: [
        `${live.bossName} rời đi ngay (còn ${clock(live.secondsLeft)} nữa mới hết giờ).`,
        "",
        `• Dừng mọi trận đang đánh (${live.fighting} trận).`,
        "• Chốt hạng theo sát thương hiện tại và gửi thư thưởng ngay.",
        "• Mọi người chơi online thấy banner boss đã rời đi.",
        "",
        "Không hoàn tác được.",
      ].join("\n"),
      color: "error",
      confirmText: "Kết thúc & chốt thưởng",
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.post(`${BASE}/end`),
      (t) => t
    );
    if (ok) onReload();
  };

  let status;
  if (live?.live) {
    status = <Chip color="error" label={`Đang diễn ra · còn ${clock(live.secondsLeft)}`} />;
  } else if (data.upcoming) {
    status = (
      <Chip
        color="warning"
        label={`${data.upcoming.bossName} xuất hiện lúc ${formatDate(data.upcoming.start)}`}
      />
    );
  } else {
    status = <Chip label="Không có lượt đang chạy" />;
  }

  return (
    <PageCard
      title="Lượt boss"
      subtitle={
        data.nextSlot
          ? `Lượt theo lịch kế tiếp: ${formatDate(data.nextSlot.start)}`
          : "Chưa có giờ nào bật — xem tab Lịch & boss"
      }
      color="primary"
      actions={
        <Tooltip title="Tải lại">
          <MDButton size="small" color="white" onClick={() => onReload()}>
            <Icon>refresh</Icon>
          </MDButton>
        </Tooltip>
      }
    >
      {data.maintenance && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Máy chủ đang đếm ngược bảo trì nên boss sẽ không tự ra theo lịch.
        </Alert>
      )}
      <MDBox mb={1}>{status}</MDBox>
      {live && (
        <MDBox>
          <MDTypography variant="h6">
            {live.bossName}
            <MDTypography component="span" variant="caption" color="text">
              {live.settled
                ? ` · đã chốt (${STATUSES[live.endStatus]?.label || live.endStatus})`
                : ""}
            </MDTypography>
          </MDTypography>
          {live.live && (
            <MDTypography variant="button" display="block">
              Pha {live.phase}/3{live.phase >= 3 ? " (nổi giận)" : ""} · sát thương người chơi ×
              {live.mul}
            </MDTypography>
          )}
          <MDTypography variant="button" display="block" mt={1}>
            Tổng sát thương {formatNumber(live.total)}
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block" mt={1}>
            {formatNumber(live.players)} người · {formatNumber(live.fights)} trận · {live.fighting}{" "}
            đang đánh · trong các khu:{" "}
            {live.lobbies.length
              ? live.lobbies.map((l) => `khu ${l.zone}: ${l.count} người`).join(", ")
              : "chưa ai vào"}
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block">
            {formatDate(live.startAt)} → {formatDate(live.endAt)}
          </MDTypography>
        </MDBox>
      )}
      {canEdit && (
        <MDBox display="flex" gap={1} mt={2} flexWrap="wrap">
          <MDButton
            size="small"
            color="error"
            variant="gradient"
            disabled={busy || Boolean(live?.live)}
            onClick={() => setDialog("summon")}
          >
            <Icon>bolt</Icon>&nbsp;Gọi boss ngoài lịch
          </MDButton>
          <MDButton
            size="small"
            color="secondary"
            variant="outlined"
            disabled={busy || !live?.live}
            onClick={end}
          >
            <Icon>stop_circle</Icon>&nbsp;Kết thúc lượt & chốt thưởng
          </MDButton>
        </MDBox>
      )}
      {dialog === "summon" && (
        <SummonDialog
          templates={data.templates}
          onClose={() => setDialog(null)}
          onDone={onReload}
        />
      )}
    </PageCard>
  );
}

LiveCard.propTypes = {
  data: PropTypes.object.isRequired,
  canEdit: PropTypes.bool.isRequired,
  onReload: PropTypes.func.isRequired,
};

function editButtons(onEdit, onDelete) {
  return (
    <MDBox display="flex" justifyContent="flex-end">
      <Tooltip title="Sửa">
        <MDButton size="small" variant="text" color="warning" onClick={onEdit}>
          <Icon>edit</Icon>
        </MDButton>
      </Tooltip>
      {onDelete && (
        <Tooltip title="Xoá">
          <MDButton size="small" variant="text" color="error" onClick={onDelete}>
            <Icon>delete</Icon>
          </MDButton>
        </Tooltip>
      )}
    </MDBox>
  );
}

const hhmm = (s) => `${String(s.hour).padStart(2, "0")}:${String(s.minute).padStart(2, "0")}`;

function rankText(r) {
  if (r.kind === KIND_MILESTONE) return `${r.rankFrom}% mục tiêu`;
  if (r.rankTo === 0) return `Hạng ${r.rankFrom} trở đi`;
  return r.rankFrom === r.rankTo ? `Hạng ${r.rankFrom}` : `Hạng ${r.rankFrom} - ${r.rankTo}`;
}

// Tab ghi lên URL (?tab=). Cấu hình là phần kỹ thuật nên để cuối.
const TABS = [
  { key: "live", label: "Đang diễn ra" },
  { key: "schedule", label: "Lịch & boss" },
  { key: "rewards", label: "Thưởng & shop" },
  { key: "history", label: "Lịch sử & tra cứu" },
  { key: "config", label: "Cấu hình (kỹ thuật)" },
];
const TAB_SX = { flex: { xs: "0 0 auto", lg: "1 1 auto" }, px: 1.5, whiteSpace: "nowrap" };

function WorldBoss() {
  const canEdit = useAuth().can(PERMISSIONS.WORLD_BOSS_EDIT);
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "live";
  const pickTab = (v) => setParams(v === "live" ? {} : { tab: v }, { replace: true });
  const overview = useFetch(() => api.get(BASE), [], 3000);
  const [historyPage, setHistoryPage] = useState(1);
  const history = useFetch(
    () => api.get(`${BASE}/history?page=${historyPage}&size=20`),
    [historyPage]
  );
  const [boardTab, setBoardTab] = useState(0);
  const [editing, setEditing] = useState(null);
  const [historyId, setHistoryId] = useState(null);
  const [run] = useRunner();
  const confirm = useConfirm();
  const data = overview.data;
  const reload = () => {
    overview.reload(true);
    history.reload(true);
  };

  const remove = async (path, title, message) => {
    const yes = await confirm({ title, message, color: "error", confirmText: "Xoá" });
    if (!yes) return;
    const { ok } = await run(
      () => api.del(`${BASE}/${path}`),
      (t) => t
    );
    if (ok) reload();
  };

  const live = data?.live;
  const edit = (kind, item = null) => setEditing({ kind, item });
  const close = () => setEditing(null);

  const board = data && (
    <PageCard
      title="Bảng sát thương"
      subtitle="Cập nhật mỗi giây. Lượt đã kết thúc giữ bảng tới lượt sau."
      color="info"
      noPadding
    >
      <MDBox px={2}>
        <Tabs value={boardTab} onChange={(e, v) => setBoardTab(v)}>
          <Tab label="Cá nhân (top 50)" />
          <Tab label="Bang (top 10)" />
        </Tabs>
      </MDBox>
      {boardTab === 0 ? (
        <AdminTable
          rows={live?.board || []}
          rowKey="userId"
          emptyText="Chưa ai gây sát thương"
          columns={[
            { key: "rank", label: "Hạng", align: "center" },
            {
              key: "name",
              label: "Người chơi",
              render: (r) => (
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Link to={`/players/${r.userId}`}>{r.name}</Link>
                  {r.online && <Chip size="small" color="success" label="online" />}
                </MDBox>
              ),
            },
            {
              key: "damage",
              label: "Sát thương",
              align: "right",
              render: (r) => formatNumber(r.damage),
            },
            { key: "fights", label: "Số trận", align: "center", render: (r) => r.fights },
            {
              key: "eligible",
              label: "Nhận thưởng?",
              render: (r) => (
                <Tooltip
                  title={
                    r.eligible
                      ? "Đã đủ số trận hoặc đủ phần sát thương để nhận thưởng"
                      : `Cần ${data.config.minFights} trận hoặc ${data.config.minDamagePermille}‰ tổng sát thương`
                  }
                >
                  <Chip
                    size="small"
                    color={r.eligible ? "success" : "default"}
                    variant={r.eligible ? "filled" : "outlined"}
                    label={r.eligible ? "Đủ điều kiện" : "Chưa đủ"}
                  />
                </Tooltip>
              ),
            },
          ]}
        />
      ) : (
        <AdminTable
          rows={live?.clans || []}
          rowKey="clanId"
          emptyText="Chưa bang nào đủ điều kiện"
          columns={[
            { key: "rank", label: "Hạng", align: "center" },
            { key: "name", label: "Bang" },
            {
              key: "damage",
              label: "Sát thương",
              align: "right",
              render: (r) => formatNumber(r.damage),
            },
            { key: "eligible", label: "Thành viên đủ điều kiện", align: "center" },
          ]}
        />
      )}
    </PageCard>
  );

  const templatesCard = data && (
    <PageCard
      title="Các boss"
      subtitle="Mỗi ngày ra con được gán cho thứ đó; Chủ nhật bốc ngẫu nhiên"
      color="error"
      noPadding
    >
      <AdminTable
        rows={data.templates}
        rowKey="bossId"
        columns={[
          {
            key: "name",
            label: "Boss",
            render: (t) => (
              <MDBox lineHeight={1.2}>
                {t.name}
                <MDTypography variant="caption" color="text" display="block">
                  hình {t.petName} · cấp {t.lvl}
                </MDTypography>
              </MDBox>
            ),
          },
          {
            key: "stats",
            label: "Công / Thủ",
            render: (t) => `${formatNumber(t.atk)} / ${formatNumber(t.def)}`,
          },
          { key: "weekdays", label: "Ra vào", wrap: true, render: (t) => dayText(t.weekdays) },
          {
            key: "enabled",
            label: "Trạng thái",
            render: (t) => {
              if (!t.loaded)
                return (
                  <Tooltip title="Kỹ năng hoặc hình boss bị lỗi, boss này không ra. Báo kỹ thuật.">
                    <Chip size="small" color="error" label="Lỗi — không ra" />
                  </Tooltip>
                );
              return (
                <Chip
                  size="small"
                  color={t.enabled ? "success" : "default"}
                  variant={t.enabled ? "filled" : "outlined"}
                  label={t.enabled ? "Bật" : "Tắt"}
                />
              );
            },
          },
          canEdit && {
            key: "actions",
            label: "",
            align: "right",
            render: (t) => editButtons(() => edit("template", t)),
          },
        ].filter(Boolean)}
      />
    </PageCard>
  );

  const schedulesCard = data && (
    <PageCard
      title="Giờ boss ra mỗi ngày"
      subtitle={`Báo trước ${data.config.warnMinutes} phút. Các khung bật không được chồng giờ nhau.`}
      color="dark"
      noPadding
      actions={
        canEdit && (
          <MDButton size="small" color="white" onClick={() => edit("schedule")}>
            <Icon>add</Icon>&nbsp;Thêm giờ
          </MDButton>
        )
      }
    >
      <AdminTable
        rows={data.schedules}
        emptyText="Chưa có giờ nào — boss sẽ không tự ra"
        columns={[
          { key: "time", label: "Giờ ra", render: (s) => hhmm(s) },
          { key: "durationMin", label: "Ở lại", render: (s) => `${s.durationMin} phút` },
          {
            key: "enabled",
            label: "Trạng thái",
            render: (s) => (
              <Chip
                size="small"
                color={s.enabled ? "success" : "default"}
                variant={s.enabled ? "filled" : "outlined"}
                label={s.enabled ? "Bật" : "Tắt"}
              />
            ),
          },
          canEdit && {
            key: "actions",
            label: "",
            align: "right",
            render: (s) =>
              editButtons(
                () => edit("schedule", s),
                () =>
                  remove(
                    `schedules/${s.id}`,
                    "Xoá giờ boss",
                    `Xoá khung ${hhmm(s)}? Từ mai boss không ra vào giờ này nữa.`
                  )
              ),
          },
        ].filter(Boolean)}
      />
    </PageCard>
  );

  const rewardsCard = data && (
    <PageCard
      title="Thưởng theo hạng"
      subtitle="Gửi vào thư lúc lượt kết thúc, chỉ cho người đủ điều kiện"
      color="warning"
      noPadding
      actions={
        canEdit && (
          <MDButton size="small" color="white" onClick={() => edit("reward")}>
            <Icon>add</Icon>&nbsp;Thêm dòng
          </MDButton>
        )
      }
    >
      <MDBox px={2} pb={1}>
        <MDTypography variant="caption" color="text">
          Đủ điều kiện = đánh ít nhất {data.config.minFights} trận, hoặc gây ít nhất{" "}
          {data.config.minDamagePermille}‰ tổng sát thương, với pet từ cấp {data.config.minPetLvl}.
        </MDTypography>
      </MDBox>
      <AdminTable
        rows={data.rewards}
        emptyText="Chưa có thưởng"
        columns={[
          {
            key: "kind",
            label: "Cho",
            render: (r) => <Chip size="small" color={KINDS[r.kind]?.color} label={r.kindName} />,
          },
          { key: "rank", label: "Hạng", render: rankText },
          {
            key: "gift",
            label: "Quà",
            wrap: true,
            render: (r) =>
              r.kind === KIND_CLAN ? (
                `${formatNumber(r.stone)} linh thạch vào kho bang`
              ) : (
                <GiftPreview lines={r.lines} />
              ),
          },
          {
            key: "enabled",
            label: "Trạng thái",
            render: (r) => (
              <Chip
                size="small"
                color={r.enabled ? "success" : "default"}
                variant={r.enabled ? "filled" : "outlined"}
                label={r.enabled ? "Đang áp dụng" : "Tắt"}
              />
            ),
          },
          canEdit && {
            key: "actions",
            label: "",
            align: "right",
            render: (r) =>
              editButtons(
                () => edit("reward", r),
                () =>
                  remove(
                    `rewards/${r.id}`,
                    "Xoá dòng thưởng",
                    `Xoá thưởng "${rankText(r)}" (${
                      r.kindName
                    })? Lượt sau người ở hạng này không nhận quà nữa.`
                  )
              ),
          },
        ].filter(Boolean)}
      />
    </PageCard>
  );

  const shopCard = data && (
    <PageCard
      title="Shop Hỗn Mang"
      subtitle="Mua bằng Hỗn Mang Tinh ở Sứ giả Hỗn Mang (map 37) và nút Shop trên màn boss"
      color="success"
      noPadding
      actions={
        canEdit && (
          <MDButton size="small" color="white" onClick={() => edit("shop")}>
            <Icon>add</Icon>&nbsp;Thêm hàng
          </MDButton>
        )
      }
    >
      <AdminTable
        rows={data.shop}
        emptyText="Shop trống"
        columns={[
          {
            key: "item",
            label: "Hàng",
            render: (r) => (
              <Tooltip title={`Mã vật phẩm #${r.itemId}`}>
                <span>
                  {r.itemName} x{r.count}
                </span>
              </Tooltip>
            ),
          },
          { key: "price", label: "Giá", align: "right", render: (r) => formatNumber(r.price) },
          {
            key: "limit",
            label: "Giới hạn",
            render: (r) => (r.limitType === 0 ? r.limitName : `${r.limitCount} lần ${r.limitName}`),
          },
          {
            key: "enabled",
            label: "",
            render: (r) => (
              <Chip
                size="small"
                color={r.enabled ? "success" : "default"}
                variant={r.enabled ? "filled" : "outlined"}
                label={r.enabled ? "Đang bán" : "Ngừng bán"}
              />
            ),
          },
          canEdit && {
            key: "actions",
            label: "",
            align: "right",
            render: (r) =>
              editButtons(
                () => edit("shop", r),
                () =>
                  remove(
                    `shop/${r.id}`,
                    "Xoá hàng khỏi shop",
                    `Xoá ${r.itemName} khỏi shop? Muốn tạm ngừng thì nên Sửa → tắt "Đang bán" thay vì xoá.`
                  )
              ),
          },
        ].filter(Boolean)}
      />
    </PageCard>
  );

  const historyCard = (
    <PageCard
      title="Các lượt đã qua"
      subtitle="Bấm vào một dòng để xem bảng xếp hạng của lượt đó"
      color="info"
      noPadding
    >
      <AdminTable
        loading={history.loading && !history.data}
        rows={history.data?.items || []}
        emptyText="Chưa có lượt nào"
        onRowClick={(h) => setHistoryId(h.id)}
        columns={[
          { key: "startAt", label: "Lúc", render: (h) => formatDate(h.startAt) },
          { key: "boss", label: "Boss", render: (h) => h.bossName },
          {
            key: "total",
            label: "Tổng sát thương",
            align: "right",
            render: (h) => formatNumber(h.total),
          },
          { key: "players", label: "Người", align: "center" },
          { key: "fights", label: "Trận", align: "center" },
          {
            key: "status",
            label: "Kết thúc do",
            render: (h) => (
              <Chip
                size="small"
                color={STATUSES[h.status]?.color || "default"}
                label={STATUSES[h.status]?.label || h.status}
              />
            ),
          },
        ]}
      />
      <MDBox display="flex" justifyContent="flex-end" gap={1} p={2}>
        <MDButton
          size="small"
          variant="outlined"
          color="info"
          disabled={historyPage <= 1}
          onClick={() => setHistoryPage((p) => p - 1)}
        >
          <Icon>chevron_left</Icon>
        </MDButton>
        <MDTypography variant="button" alignSelf="center">
          Trang {historyPage} / {Math.max(1, Math.ceil((history.data?.total || 0) / 20))}
        </MDTypography>
        <MDButton
          size="small"
          variant="outlined"
          color="info"
          disabled={historyPage * 20 >= (history.data?.total || 0)}
          onClick={() => setHistoryPage((p) => p + 1)}
        >
          <Icon>chevron_right</Icon>
        </MDButton>
      </MDBox>
    </PageCard>
  );

  const configCard = data && (
    <PageCard
      title="Cấu hình trận đánh"
      subtitle="Ảnh hưởng độ khó và điều kiện nhận thưởng. Chỉ sửa khi người cân bằng game yêu cầu."
      color="warning"
      actions={
        canEdit && (
          <MDButton size="small" color="white" onClick={() => edit("config", data.config)}>
            <Icon>edit</Icon>&nbsp;Sửa
          </MDButton>
        )
      }
    >
      <Grid container columnSpacing={4}>
        {CONFIG_FIELDS.map(([key, label]) => (
          <Grid item xs={12} md={6} key={key}>
            <MDBox display="flex" justifyContent="space-between" py={0.25}>
              <MDTypography variant="caption" color="text">
                {label}
              </MDTypography>
              <MDTypography variant="caption" fontWeight="medium">
                {formatNumber(data.config[key])}
              </MDTypography>
            </MDBox>
          </Grid>
        ))}
        <Grid item xs={12}>
          <MDBox display="flex" justifyContent="space-between" py={0.25} gap={2}>
            <MDTypography variant="caption" color="text">
              Hệ số sát thương theo thời gian còn lại
            </MDTypography>
            <MDTypography variant="caption" fontWeight="medium" sx={{ wordBreak: "break-all" }}>
              {data.config.damageMulSteps}
            </MDTypography>
          </MDBox>
        </Grid>
      </Grid>
    </PageCard>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="worldBoss" />
        {overview.error && <Alert severity="error">{overview.error}</Alert>}
        <Tabs
          value={tab}
          onChange={(e, v) => pickTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3, maxWidth: 900 }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} sx={TAB_SX} />
          ))}
        </Tabs>
        {data && tab === "live" && (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={4}>
              <LiveCard data={data} canEdit={canEdit} onReload={reload} />
            </Grid>
            <Grid item xs={12} lg={8}>
              {board}
            </Grid>
          </Grid>
        )}
        {data && tab === "schedule" && (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={5}>
              {schedulesCard}
            </Grid>
            <Grid item xs={12} lg={7}>
              {templatesCard}
            </Grid>
          </Grid>
        )}
        {data && tab === "rewards" && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {rewardsCard}
            </Grid>
            <Grid item xs={12}>
              {shopCard}
            </Grid>
          </Grid>
        )}
        {tab === "history" && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <PlayerCard />
            </Grid>
            <Grid item xs={12}>
              {historyCard}
            </Grid>
          </Grid>
        )}
        {data && tab === "config" && configCard}
      </MDBox>
      {editing?.kind === "config" && (
        <ConfigDialog config={editing.item} onClose={close} onDone={reload} />
      )}
      {editing?.kind === "template" && (
        <TemplateDialog template={editing.item} onClose={close} onDone={reload} />
      )}
      {editing?.kind === "schedule" && (
        <ScheduleDialog schedule={editing.item} onClose={close} onDone={reload} />
      )}
      {editing?.kind === "reward" && (
        <RewardDialog reward={editing.item} onClose={close} onDone={reload} />
      )}
      {editing?.kind === "shop" && (
        <ShopDialog
          row={editing.item}
          limitTypes={data?.limitTypes || []}
          onClose={close}
          onDone={reload}
        />
      )}
      {historyId && <HistoryDialog id={historyId} onClose={() => setHistoryId(null)} />}
      <Footer />
    </DashboardLayout>
  );
}

export default WorldBoss;
